import React, {useState} from "react";
import styles from "./ToolMessage.module.css";
import {AgentName, ChatHistoryDTO} from "../../../../../../../api/generated";
import {ToolCallInfo, ToolCallInfoComponent} from "./components/toolCallInfo/ToolCallInfoComponent";
import {Arrow} from "../../../../../../elements/arrow/Arrow";

interface ToolMessageProps {
    chatHistoryId: number
    chatHistoryList: ChatHistoryDTO[]
}

export const ToolMessage: React.FC<ToolMessageProps> = ({
                                                            chatHistoryId,
                                                            chatHistoryList
                                                        }) => {
    const agentNameLabels: Record<AgentName, string> = {
        [AgentName.UsedVehicleSearch]: "Used Vehicle Search",
        [AgentName.NewVehicleSearch]: "New Vehicle Search",
        [AgentName.Booking]: "Booking",
        [AgentName.Requests]: "Requests",
        [AgentName.Aftersales]: "Aftersales",
        [AgentName.Knowledge]: "Knowledge",
        [AgentName.PxValuation]: "PX Valuation",
        [AgentName.Finance]: "Finance",
        [AgentName.Reservation]: "Reservation",
        [AgentName.UtilityHandover]: "Utility Handover",
    }

    const getAgentName = (agentName: AgentName): string => {
        return agentNameLabels[agentName]
    }


    const index = chatHistoryList.findIndex(ch => ch.id === chatHistoryId);
    if (index === -1) {
        console.error(`Failed to find chatHistory by id: ${chatHistoryId}`)
        throw new Error(`Failed to find chatHistory by id: ${chatHistoryId}`)
    }

    const assistantToolCallMessage = chatHistoryList[index]
    const toolCalls = assistantToolCallMessage.toolCalls

    if (!toolCalls || toolCalls.length < 1) {
        console.error("numberOfToolCalls can not be less than 1")
        throw new Error("numberOfToolCalls can not be less than 1")
    }

    const numberOfToolCalls = toolCalls.length;
    const toolResponses = chatHistoryList.slice(index + 1, numberOfToolCalls + index + 1)
    const handoffToolName = "handoff_to_agent";

    const parseJsonString = (value: string) => {
        try {
            return JSON.parse(value)
        } catch (e) {
            return value
        }
    }

    const isAgentName = (value: unknown): value is AgentName => {
        return typeof value === "string" && Object.values(AgentName).includes(value as AgentName)
    }

    const getTargetAgent = (serializedArguments?: string): AgentName | undefined => {
        if (!serializedArguments) {
            return undefined
        }

        const parsedArguments = parseJsonString(serializedArguments)
        const normalizedArguments = typeof parsedArguments === "string"
            ? parseJsonString(parsedArguments)
            : parsedArguments

        if (!normalizedArguments || typeof normalizedArguments !== "object") {
            return undefined
        }

        const argsMap = normalizedArguments as Record<string, unknown>
        const targetAgent = argsMap.target_agent ?? argsMap.targetAgent
        return isAgentName(targetAgent) ? targetAgent : undefined
    }

    const [toolCallInfoList, setToolCallInfoList] = useState<ToolCallInfo[]>(
        toolCalls.map((toolCall, index) => ({
            isOpen: false,
            toolCall,
            toolResponse: toolResponses[index]?.toolResponse ?? "",
            isAgentHandover: toolCall.name === handoffToolName,
            targetAgent: getTargetAgent(toolCall.arguments),
        } as ToolCallInfo))
    )

    const toggleTool = (tool: ToolCallInfo) => {
        setToolCallInfoList(prevTools =>
            prevTools.map(t =>
                t.toolCall.callId === tool.toolCall.callId
                    ? {...t, isOpen: !t.isOpen}
                    : t
            )
        );
    }

    const normalizeToolName = (toolCallInfo: ToolCallInfo): string => {
        if (toolCallInfo.isAgentHandover) {
            const targetAgent = toolCallInfo.targetAgent!;

            return `Handoff to ${getAgentName(targetAgent)} Agent`
        } else {
            return toolCallInfo.toolCall.name.replaceAll("_", " ")
        }
    }

    return <div className={styles.wrapper}>
        {toolCallInfoList?.map(toolCallInfo => (
            <div key={toolCallInfo.toolCall.callId} className={styles.toolCallElementWrapper}>
                <div className={`${styles.toolCallPreview} ${toolCallInfo.isAgentHandover ? styles.handoverToolCallPreview : ""}`}>
                    <div className={styles.line}/>
                    <div
                        className={`${styles.toolNameWrapper} ${toolCallInfo.isAgentHandover ? styles.handoverToolNameWrapper : ""}`}
                        onClick={() => toggleTool(toolCallInfo)}
                    >
                        <img src={"/toolCallIcon.svg"} alt="Tool Call"/>
                        <span className={styles.toolName}>{normalizeToolName(toolCallInfo)}</span>
                        <Arrow isUp={toolCallInfo.isOpen} onClick={() => toggleTool(toolCallInfo)}/>
                    </div>
                    <div className={styles.line}/>
                </div>
                <ToolCallInfoComponent toolCallInfo={toolCallInfo}/>
            </div>
        ))}
    </div>
}
